import {
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format, parseISO } from "date-fns";
import React from "react";
import { TripWithTenant } from "../../../types";
import { SCHEDULE_MIN_DATE } from "../../../utils/dateConstants";
import { HangarIcon } from "../../../widgets/Icons/HangarIcon";
import { RampIcon } from "../../../widgets/Icons/RampIcon";

interface LocationOption {
  id: string;
  name: string;
  type: "hangar" | "ramp";
}

interface AssignLocationDialogPresenterProps {
  // Dialog state
  open: boolean;
  onClose: () => void;

  // Trip data
  trip: TripWithTenant | null;
  currentLocation?: string | null;

  // Loading and error state
  loading: boolean;
  assigning: boolean;
  error: string | null;
  setError: (error: string | null) => void;

  // Location data
  locations: LocationOption[];
  selectedLocation: string;
  setSelectedLocation: (location: string) => void;

  // Date range state
  useCustomDates: boolean;
  setUseCustomDates: (use: boolean) => void;
  startTime: Date | null;
  setStartTime: (date: Date | null) => void;
  endTime: Date | null;
  setEndTime: (date: Date | null) => void;

  // Action handler
  handleAssign: () => void;
}

export const AssignLocationDialogPresenter: React.FC<AssignLocationDialogPresenterProps> = ({
  open,
  onClose,
  trip,
  currentLocation,
  loading,
  assigning,
  error,
  setError,
  locations,
  selectedLocation,
  setSelectedLocation,
  useCustomDates,
  setUseCustomDates,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  handleAssign,
}) => {
  const [showCustomDates, setShowCustomDates] = React.useState(false);

  // Group locations by type
  const hangars = locations.filter((l) => l.type === "hangar");
  const ramps = locations.filter((l) => l.type === "ramp");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
        elevation: 1,
      }}
      sx={{
        zIndex: 9600,
      }}
    >
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6">Assign Aircraft to Location</Typography>
          {trip && (
            <Typography variant="body2" color="text.secondary">
              {trip.tail_number} •{" "}
              {format(parseISO(trip.arrival_date), "MMM d")} -{" "}
              {format(parseISO(trip.departure_date), "MMM d, yyyy")}
            </Typography>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack spacing={0.5}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={40}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>
        ) : (
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {/* Unassigned Option */}
              <Box
                onClick={() => setSelectedLocation("unassigned")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  bgcolor:
                    selectedLocation === "unassigned"
                      ? "primary.50"
                      : "transparent",
                  "&:hover": {
                    bgcolor:
                      selectedLocation === "unassigned"
                        ? "primary.50"
                        : "grey.50",
                  },
                }}
              >
                <FormControlLabel
                  value="unassigned"
                  control={<Radio size="small" />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2">
                        Keep Unassigned
                      </Typography>
                      {currentLocation === null && (
                        <Chip
                          label="Current"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                    </Stack>
                  }
                  sx={{ m: 0, width: "100%", pointerEvents: "none" }}
                />
              </Box>
              <Divider sx={{ my: 1.5 }} />

              {/* Hangars Section */}
              {hangars.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.5,
                      fontWeight: 600,
                      color: "text.secondary",
                      px: 1.5,
                    }}
                  >
                    <HangarIcon sx={{ fontSize: 16 }} />
                    Hangars
                  </Typography>
                  <Stack spacing={0.5}>
                    {hangars.map((location) => (
                      <Box
                        key={location.id}
                        onClick={() => setSelectedLocation(location.id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          cursor: "pointer",
                          bgcolor:
                            selectedLocation === location.id
                              ? "primary.50"
                              : "transparent",
                          "&:hover": {
                            bgcolor:
                              selectedLocation === location.id
                                ? "primary.50"
                                : "grey.50",
                          },
                        }}
                      >
                        <FormControlLabel
                          value={location.id}
                          control={<Radio size="small" />}
                          label={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ width: "100%" }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ flexGrow: 1 }}
                              >
                                {location.name}
                              </Typography>
                              {currentLocation === location.id && (
                                <Chip
                                  label="Current"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Stack>
                          }
                          sx={{ m: 0, width: "100%", pointerEvents: "none" }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Ramps Section */}
              {ramps.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.5,
                      fontWeight: 600,
                      color: "text.secondary",
                      px: 1.5,
                    }}
                  >
                    <RampIcon sx={{ fontSize: 16 }} />
                    Ramps
                  </Typography>
                  <Stack spacing={0.5}>
                    {ramps.map((location) => (
                      <Box
                        key={location.id}
                        onClick={() => setSelectedLocation(location.id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          cursor: "pointer",
                          bgcolor:
                            selectedLocation === location.id
                              ? "primary.50"
                              : "transparent",
                          "&:hover": {
                            bgcolor:
                              selectedLocation === location.id
                                ? "primary.50"
                                : "grey.50",
                          },
                        }}
                      >
                        <FormControlLabel
                          value={location.id}
                          control={<Radio size="small" />}
                          label={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ width: "100%" }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ flexGrow: 1 }}
                              >
                                {location.name}
                              </Typography>
                              {currentLocation === location.id && (
                                <Chip
                                  label="Current"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Stack>
                          }
                          sx={{ m: 0, width: "100%", pointerEvents: "none" }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* No locations available message */}
              {locations.length === 0 && !loading && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No locations available for the selected time period.
                </Alert>
              )}
            </RadioGroup>
          </FormControl>
        )}

        {/* Custom Date Range Section - Collapsible */}
        {trip && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setShowCustomDates(!showCustomDates);
                if (!showCustomDates) {
                  setUseCustomDates(true);
                } else {
                  setUseCustomDates(false);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  transform: showCustomDates ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
              Custom time range
            </Link>
            <Collapse in={showCustomDates}>
              <Box sx={{ mt: 1.5 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Stack spacing={1.5}>
                    <DateTimePicker
                      label="Start Time"
                      value={startTime}
                      onChange={(value) => setStartTime(value)}
                      minDate={SCHEDULE_MIN_DATE}
                      minDateTime={new Date(trip.arrival_date)}
                      maxDateTime={endTime || new Date(trip.departure_date)}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                        },
                      }}
                    />
                    <DateTimePicker
                      label="End Time"
                      value={endTime}
                      onChange={(value) => setEndTime(value)}
                      minDate={SCHEDULE_MIN_DATE}
                      minDateTime={startTime || new Date(trip.arrival_date)}
                      maxDateTime={new Date(trip.departure_date)}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                        },
                      }}
                    />
                  </Stack>
                </LocalizationProvider>

                {/* Warning about gaps that will be created */}
                {startTime &&
                  endTime &&
                  selectedLocation &&
                  selectedLocation !== "unassigned" &&
                  (startTime > new Date(trip.arrival_date) ||
                    endTime < new Date(trip.departure_date)) && (
                    <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ display: "block" }}
                      >
                        Unassigned periods will be created before/after this time range
                      </Typography>
                    </Alert>
                  )}
              </Box>
            </Collapse>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} disabled={assigning} size="small">
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={assigning || loading || !selectedLocation}
          startIcon={
            assigning ? <CircularProgress size={16} /> : <LocationIcon />
          }
          size="small"
        >
          {assigning ? "Assigning..." : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};